<template>
  <CustomModal
    :title="title"
    :visible="visible"
    :width="width"
    @update:visible="emit('update:visible', $event)"
  >
    <div class="flex flex-col gap-6">
      <div class="form_col">
        <span class="secondary_color text-[13px]"
          >{{ featureNames.SEQUENCES }} Name</span
        >
        <CustomInput
          v-model="newSequenceName"
          :placeholder="`Enter new ${featureNames.SEQUENCES} name`"
          class="w-full"
          :autoFocus="true"
        />
      </div>

      <div class="form_col">
        <CustomSelector
          source="create_sequence"
          :options="sequenceCreationSelectorOptions"
          v-model="sequenceType"
          direction="vertical"
        />
      </div>

      <div class="form_col">
        <span class="secondary_color text-[13px]">Who can access</span>
        <a-select v-model:value="newSequencePermission" class="w-full">
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_arrow"
              class="size-4"
            />
          </template>
          <a-select-option
            v-for="(permission, index) in permissionOptions"
            :key="index"
            :value="permission?.value"
            :label="permission?.label"
          >
            {{ permission?.label }}
          </a-select-option>
        </a-select>
      </div>
      <div class="form_col">
        <span class="secondary_color text-[13px]">Tags</span>
        <a-select
          v-model:value="tags"
          mode="tags"
          class="w-full"
          :placeholder="`Add tags to organize your ${featureNames.SEQUENCES}`"
          :tokenSeparators="[',']"
          :options="tagOptions"
          :fieldNames="{ label: 'label', value: 'value' }"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_arrow"
              class="size-4"
            />
          </template>
        </a-select>
      </div>
    </div>

    <template #footer>
      <a-button
        @click="handleContinue"
        class="primary_medium ml-2"
        :loading="isLoading"
        :disabled="!newSequenceName?.trim()"
        >Continue
      </a-button>
    </template>
  </CustomModal>
</template>

<script setup>
import { permissionOptions } from "@/src/constants/sequenceConstants.js";
import CustomInput from "@/src/components/CustomInput.vue";
import { ref, watch } from "vue";
import { message } from "ant-design-vue";
import SequenceService from "@/src/api/SequenceService.js";
import { useRouter } from "vue-router";
import CustomSelector from "@/src/components/CustomSelector.vue";
import { sequenceCreationSelectorOptions } from "@/src/constants/sequenceConstants.js";
import CustomModal from "@/src/components/CustomModal.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  visible: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "400px",
  },
  addAction: {
    type: Boolean,
    default: true,
  },
  showMessage: {
    type: Boolean,
    default: false,
  },
  initialTags: {
    type: Array,
    default: () => [],
  },
  navigateAfterCreation: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:visible", "sequence-created"]);

const router = useRouter();
const isLoading = ref(false);
const newSequenceName = ref("");
const newSequencePermission = ref("team_edit");
const tags = ref([]);
const tagOptions = ref([]);
const newEntityList = ref({
  entityType: "contact",
});
const sequenceType = ref("choose_template");

const loadTags = async () => {
  try {
    const response = await SequenceService?.getTagsForSequenceFilters();
    if (response?.success && response?.tags) {
      tagOptions.value = response?.tags?.map((tag) => ({
        label: tag?.name,
        value: tag?.id,
      }));
    }
  } catch (error) {
    console.error("Error loading tags:", error);
  }
};

watch(
  () => props?.visible,
  (newVal) => {
    if (newVal) {
      tags.value = [...props?.initialTags];
      loadTags();
    }
  },
  { immediate: true },
);

const closeModal = async () => {
  emit("update:visible", false);
  newSequenceName.value = "";
  tags.value = [];
  sequenceType.value = "choose_template";
};

const handleContinue = async () => {
  if (!newSequenceName?.value) {
    message.error(`Please enter ${featureNames.SEQUENCES} name`);
    return;
  }

  if (sequenceType?.value === "choose_template") {
    router?.push({
      name: "sequence_templates",
      query: {
        name: newSequenceName?.value,
        permission: newSequencePermission?.value,
        tags: JSON.stringify(tags?.value),
      },
    });
    await closeModal();
  } else {
    await createSequence();
  }
};

const createSequence = async () => {
  if (!newSequenceName?.value) {
    message.error(`Please enter ${featureNames.SEQUENCES} name`);
    return;
  }

  isLoading.value = true;
  try {
    const payload = {
      name: newSequenceName?.value,
      permission: newSequencePermission?.value,
      tags: tags?.value,
      sequenceType: sequenceType?.value,
      entityType: newEntityList?.value?.entityType,
    };
    const response = await SequenceService?.createSequence(payload);
    if (response?.success) {
      if (props?.showMessage) {
        message.success(`${featureNames.SEQUENCES} created successfully`);
      }
      if (props?.addAction) {
        emit("sequence-created", {
          sequenceId: response?.sequence?.id,
          tags: tags?.value,
          sequenceType: sequenceType?.value,
        });
      }

      if (props?.navigateAfterCreation) {
        await router?.push({
          name: "sequence-edit",
          params: { id: response?.sequence?.id },
        });
      }
    } else {
      if (props?.showMessage) {
        message.error(`Failed to create ${featureNames.SEQUENCES}`);
      }
    }
  } catch (error) {
    console.log(`Error creating ${featureNames.SEQUENCES}:`, error);
    if (props?.showMessage) {
      message.error(
        `An error occurred while creating ${featureNames.SEQUENCES}`,
      );
    }
  } finally {
    isLoading.value = false;
    await closeModal();
  }
};
</script>
